import React, { useState, useEffect, useCallback } from 'react';

/**
 * ImageLightbox - Visor de imagenes y videos con navegacion de galeria
 *
 * Modos de uso:
 *
 * 1. Modo legacy (single image):
 *    <ImageLightbox src="/cdn/img.png" alt="Producto" onClose={fn} />
 *
 * 2. Modo galeria (multiple items):
 *    <ImageLightbox
 *      items={[
 *        { type: 'image', url: '/cdn/img1.png' },
 *        { type: 'image', url: '/cdn/img2.png' },
 *        { type: 'video', url: '/cdn/video.mp4' }
 *      ]}
 *      initialIndex={0}
 *      onClose={fn}
 *    />
 */
const ImageLightbox = ({ src, alt, items, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Normalizar items: si se pasa src (modo legacy), convertir a array
  const mediaItems = items || (src ? [{ type: 'image', url: src }] : []);
  const total = mediaItems.length;

  const goNext = useCallback(() => {
    if (total > 1) setCurrentIndex(prev => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    if (total > 1) setCurrentIndex(prev => (prev - 1 + total) % total);
  }, [total]);

  // Teclado: flechas + escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goNext, goPrev]);

  if (mediaItems.length === 0) return null;

  const current = mediaItems[currentIndex];
  const isVideo = current.type === 'video' || (current.url && (current.url.endsWith('.mp4') || current.url.endsWith('.3gp')));

  return (
    <div className="image-lightbox" onClick={onClose}>
      {/* Boton cerrar */}
      <button className="image-lightbox-close" onClick={onClose}>
        <i className="bi bi-x-lg"></i>
      </button>

      {/* Indicador de posicion */}
      {total > 1 && (
        <div
          style={{
            position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 14px',
            borderRadius: '20px', fontSize: '0.85rem', zIndex: 10001
          }}
        >
          {currentIndex + 1} de {total}
        </div>
      )}

      {/* Flecha izquierda */}
      {total > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          style={{
            position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none',
            borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.2rem', zIndex: 10001, transition: 'background 0.2s'
          }}
          onMouseEnter={e => e.target.style.background = 'rgba(0,0,0,0.8)'}
          onMouseLeave={e => e.target.style.background = 'rgba(0,0,0,0.5)'}
        >
          <i className="bi bi-chevron-left"></i>
        </button>
      )}

      {/* Contenido: imagen o video */}
      {isVideo ? (
        <video
          key={current.url}
          src={current.url}
          controls
          autoPlay
          onClick={e => e.stopPropagation()}
          style={{
            maxWidth: '90vw', maxHeight: '85vh', borderRadius: '8px',
            background: '#000'
          }}
        />
      ) : (
        <img
          key={current.url}
          src={current.url}
          alt={alt || `Media ${currentIndex + 1}`}
          onClick={e => e.stopPropagation()}
        />
      )}

      {/* Flecha derecha */}
      {total > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          style={{
            position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none',
            borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.2rem', zIndex: 10001, transition: 'background 0.2s'
          }}
          onMouseEnter={e => e.target.style.background = 'rgba(0,0,0,0.8)'}
          onMouseLeave={e => e.target.style.background = 'rgba(0,0,0,0.5)'}
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      )}

      {/* Thumbnails en la parte inferior */}
      {total > 1 && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: '6px', padding: '6px 10px',
            background: 'rgba(0,0,0,0.5)', borderRadius: '10px', zIndex: 10001,
            maxWidth: '90vw', overflowX: 'auto'
          }}
        >
          {mediaItems.map((item, idx) => {
            const isThumbVideo = item.type === 'video' || (item.url && (item.url.endsWith('.mp4') || item.url.endsWith('.3gp')));
            return (
              <div
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                style={{
                  width: '40px', height: '40px', borderRadius: '4px', cursor: 'pointer',
                  border: idx === currentIndex ? '2px solid #fff' : '2px solid transparent',
                  overflow: 'hidden', flexShrink: 0, position: 'relative',
                  background: '#333'
                }}
              >
                {isThumbVideo ? (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#222' }}>
                    <i className="bi bi-play-fill text-white" style={{ fontSize: '0.8rem' }}></i>
                  </div>
                ) : (
                  <img src={item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
